import {
  Badge,
  Button,
  Card,
  ConfigProvider,
  Popconfirm,
  Result,
  Segmented,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import ConfiguracaoCard from "./ConfiguracaoCard";
import EnvioCard from "./EnvioCard";
import GenerateNowModal from "./GenerateNowModal";
import RotinasExcluidas, { HistoricoDaRotina } from "./RotinasExcluidas";
import RoutineFormModal from "./RoutineFormModal";
import RunsCard from "./RunsCard";
import { describeError, formatDateTime } from "./status";

const { Text } = Typography;
const DIAS = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const ABAS = ["rotinas", "execucoes", "whatsapp", "configuracoes"];
const ABAS_DE_GESTAO = ["whatsapp", "configuracoes"];

const ATIVA = { rotulo: "Ativa", cor: "#52c41a" };
const INATIVA = { rotulo: "Inativa", cor: "#ff4d4f" };
const AUTOMATICO = { rotulo: "Envio automático", cor: "#1677ff" };
const MANUAL = { rotulo: "Envio manual", cor: "#faad14" };
const LEGENDA = [ATIVA, INATIVA, AUTOMATICO, MANUAL];

export function estadoDaRotina(rotina) {
  return [
    rotina.is_active ? ATIVA : INATIVA,
    rotina.entrega_automatica ? AUTOMATICO : MANUAL,
  ];
}

export default function AiReports() {
  const { user } = useAuth();
  const {
    getAiReportsCatalog,
    getAiReportRoutines,
    deleteAiReportRoutine,
    updateAiReportRoutine,
  } = useApi();
  const [catalog, setCatalog] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formRotina, setFormRotina] = useState({ open: false });
  const [contatoMudou, setContatoMudou] = useState(false);
  const [gerar, setGerar] = useState(null);
  const [visao, setVisao] = useState("em_uso");
  const [parametros, setParametros] = useSearchParams();

  const podeVer = Boolean(user?.is_superuser);
  const podeGerenciar = Boolean(catalog?.pode_gerenciar);
  const pedida = parametros.get("aba");
  const aba =
    ABAS.includes(pedida) && (!ABAS_DE_GESTAO.includes(pedida) || podeGerenciar)
      ? pedida
      : "rotinas";

  const load = async () => {
    setLoading(true);
    try {
      const [cat, rot] = await Promise.all([
        getAiReportsCatalog(),
        getAiReportRoutines(),
      ]);
      setCatalog(cat);
      setRoutines(Array.isArray(rot) ? rot : []);
    } catch (err) {
      notification.error({
        message: "Erro ao carregar relatórios por IA.",
        description: describeError(err, ""),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (podeVer) load();
  }, [podeVer]);

  const rotinaPedida = parametros.get("rotina");
  useEffect(() => {
    if (!rotinaPedida || !routines.length) return;
    const rotina = routines.find((r) => String(r.id) === rotinaPedida);
    if (rotina) setFormRotina({ open: true, editing: rotina });
    const proximos = new URLSearchParams(parametros);
    proximos.delete("rotina");
    setParametros(proximos, { replace: true });
  }, [rotinaPedida, routines]);

  const fecharRotina = async () => {
    setFormRotina({ open: false });
    if (!contatoMudou) return;
    setContatoMudou(false);
    try {
      setCatalog(await getAiReportsCatalog());
    } catch (err) {
      notification.error({
        message: "Erro ao recarregar os contatos.",
        description: describeError(err, ""),
      });
    }
  };

  if (!podeVer) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="Esta área é exclusiva para superadmins."
      />
    );
  }

  const excluir = async (rotina) => {
    try {
      await deleteAiReportRoutine(rotina.id);
      notification.success({
        message: `Rotina ${rotina.name} excluída.`,
        description: "Ela está em Excluídas e pode ser restaurada.",
      });
      await load();
    } catch (err) {
      notification.error({
        message: "Erro ao excluir.",
        description: describeError(err, ""),
      });
    }
  };

  const alternarAtiva = async (rotina) => {
    try {
      const salva = await updateAiReportRoutine(rotina.id, {
        is_active: !rotina.is_active,
      });
      setRoutines((atuais) =>
        atuais.map((r) => (r.id === rotina.id ? { ...r, ...salva } : r))
      );
      notification.success({
        message: rotina.is_active
          ? `Rotina ${rotina.name} inativada.`
          : `Rotina ${rotina.name} ativada.`,
      });
    } catch (err) {
      notification.error({
        message: rotina.is_active ? "Erro ao inativar." : "Erro ao ativar.",
        description: describeError(err, ""),
      });
    }
  };

  const filiais = Object.fromEntries(
    (catalog?.branches || []).map((b) => [b.id, b.name])
  );

  const colunas = [
    {
      title: "Rotina",
      dataIndex: "name",
      key: "name",
      render: (v, r) => (
        <Space size={8} align="baseline">
          <Space size={2}>
            {estadoDaRotina(r).map((estado) => (
              <Tooltip key={estado.rotulo} title={estado.rotulo}>
                <Badge color={estado.cor} aria-label={estado.rotulo} />
              </Tooltip>
            ))}
          </Space>
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    { title: "Cliente", dataIndex: "client_name", key: "client" },
    {
      title: "Filiais",
      dataIndex: "branches",
      key: "branches",
      render: (ids) =>
        ids && ids.length ? (
          ids.map((id) => filiais[id] || id).join(", ")
        ) : (
          <Text type="danger">nenhuma filial</Text>
        ),
    },
    {
      title: "Agenda",
      key: "agenda",
      render: (_, r) =>
        r.periodicidade === "mensal"
          ? `mensal, dia ${r.dia_execucao} às ${r.hora_execucao?.slice(0, 5)}`
          : `semanal, ${DIAS[r.dia_execucao]} às ${r.hora_execucao?.slice(0, 5)} (${r.janela_dias} dias)`,
    },
    {
      title: "Próxima",
      dataIndex: "proxima_execucao",
      key: "proxima",
      render: (v, r) => (r.is_active ? formatDateTime(v) : <Tag>inativa</Tag>),
    },
    {
      title: "Último erro",
      dataIndex: "ultimo_erro",
      key: "erro",
      ellipsis: true,
      render: (v) => (v ? <Text type="danger">{v}</Text> : "—"),
    },
    {
      title: "Ações",
      key: "actions",
      width: podeGerenciar ? 300 : 120,
      render: (_, r) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            disabled={!r.is_active}
            onClick={() => setGerar(r)}>
            Gerar agora
          </Button>
          {podeGerenciar && (
            <Button
              type="link"
              size="small"
              onClick={() => setFormRotina({ open: true, editing: r })}>
              Editar
            </Button>
          )}
          {podeGerenciar && (
            <Popconfirm
              title={r.is_active ? "Inativar rotina?" : "Ativar rotina?"}
              description={
                r.is_active
                  ? "Ela para de gerar e de enviar relatórios até ser ativada de novo."
                  : r.entrega_automatica
                    ? "Ela volta a gerar no horário da agenda e envia sozinha aos contatos."
                    : "Ela volta a gerar no horário da agenda. O envio continua pelo botão Enviar da execução."
              }
              onConfirm={() => alternarAtiva(r)}
              okText={r.is_active ? "Inativar" : "Ativar"}
              cancelText="Cancelar">
              <Button type="link" size="small">
                {r.is_active ? "Inativar" : "Ativar"}
              </Button>
            </Popconfirm>
          )}
          {podeGerenciar && <HistoricoDaRotina historico={r.historico} />}
          {podeGerenciar && (
            <Popconfirm
              title="Excluir rotina?"
              description="Ela sai da lista, fica inativa e para de enviar. As execuções continuam no histórico, e dá para restaurar em Excluídas."
              onConfirm={() => excluir(r)}
              okText="Excluir"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar">
              <Button type="link" size="small" danger>
                Excluir
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div style={{ width: "94%" }}>
          <Tabs
            activeKey={aba}
            onChange={(chave) => setParametros({ aba: chave })}
            items={[
              {
                key: "rotinas",
                label: "Rotinas",
                children: (
                  <Card
                    title="Rotinas de relatório por IA"
                    extra={
                      podeGerenciar && (
                        <Space>
                          <Segmented
                            value={visao}
                            onChange={setVisao}
                            options={[
                              { value: "em_uso", label: "Em uso" },
                              { value: "excluidas", label: "Excluídas" },
                            ]}
                          />
                          {visao === "em_uso" && (
                            <Button
                              type="primary"
                              onClick={() => setFormRotina({ open: true })}>
                              Nova rotina
                            </Button>
                          )}
                        </Space>
                      )
                    }>
                    {visao === "excluidas" ? (
                      <RotinasExcluidas filiais={filiais} onRestaurada={load} />
                    ) : (
                      <>
                        <Space size={16} wrap style={{ marginBottom: 12 }}>
                          {LEGENDA.map((estado) => (
                            <Badge
                              key={estado.rotulo}
                              color={estado.cor}
                              text={
                                <Text type="secondary">{estado.rotulo}</Text>
                              }
                            />
                          ))}
                        </Space>
                        <Table
                          dataSource={routines}
                          columns={colunas}
                          rowKey="id"
                          loading={loading}
                          pagination={false}
                          size="middle"
                          locale={{ emptyText: "Nenhuma rotina cadastrada." }}
                        />
                      </>
                    )}
                  </Card>
                ),
              },
              {
                key: "execucoes",
                label: "Execuções",
                children: (
                  <Card title="Execuções">
                    <RunsCard catalog={catalog} routines={routines} />
                  </Card>
                ),
              },
              ...(podeGerenciar
                ? [
                    {
                      key: "whatsapp",
                      label: (
                        <Badge
                          dot={catalog ? !catalog.entrega_configurada : false}
                          offset={[6, 0]}>
                          WhatsApp
                        </Badge>
                      ),
                      children: (
                        <EnvioCard
                          habilitado={Boolean(catalog?.entrega_habilitada)}
                          onMudou={load}
                        />
                      ),
                    },
                    {
                      key: "configuracoes",
                      label: "Configurações",
                      children: <ConfiguracaoCard />,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
      <RoutineFormModal
        open={formRotina.open}
        editing={formRotina.editing}
        catalog={catalog}
        onClose={fecharRotina}
        onSaved={() => {
          setContatoMudou(false);
          load();
        }}
        onContatoSalvo={() => setContatoMudou(true)}
      />
      <GenerateNowModal
        open={Boolean(gerar)}
        routine={gerar}
        catalog={catalog}
        onClose={() => setGerar(null)}
      />
    </ConfigProvider>
  );
}
